import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Presentation,
  PanelTop,
  LayoutGrid,
  MonitorPlay,
  Tablet,
  Lightbulb,
  TrainFront,
  Armchair,
  Gauge,
  Building2,
  Tv,
  Eye,
  Users,
  Target,
  IndianRupee,
  Sparkles,
  Video,
  Clock,
  Smartphone,
  Zap,
  Star,
  Megaphone,
  HeartPulse,
  BarChart3,
  Flag,
  TrendingUp,
  Monitor,
} from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, MaskLines } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";
import { IMAGES, RAILWAY_NETWORK, RAILWAY_MEDIA } from "@/lib/data";

const ICON_MAP = {
  Presentation,
  PanelTop,
  LayoutGrid,
  MonitorPlay,
  Tablet,
  Lightbulb,
  TrainFront,
  Armchair,
  Gauge,
  Building2,
  Tv,
  Eye,
  Users,
  Target,
  IndianRupee,
  Sparkles,
  Video,
  Clock,
  Smartphone,
  Zap,
  Star,
  Megaphone,
  HeartPulse,
  BarChart3,
  Flag,
  TrendingUp,
  Monitor,
};

const Overline = ({ children, dark }) => (
  <span className={`inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] ${dark ? "text-gold" : "text-maroon"}`}>
    <span className={`h-px w-8 ${dark ? "bg-gold" : "bg-maroon"}`} />
    {children}
  </span>
);

const PageHero = () => {
  const stationCount = RAILWAY_NETWORK.reduce((sum, s) => sum + s.stations.length, 0);
  const stateCount = RAILWAY_NETWORK.length;

  return (
    <section className="relative pt-40 md:pt-52 pb-16 md:pb-20 overflow-hidden bg-maroon text-cream">
      <div className="absolute inset-0 -z-10 opacity-25">
        <img src={IMAGES.coverageMap} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-maroon/75" />
      </div>
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-gold">
          <span className="h-px w-8 bg-gold" />Our Network
        </motion.span>
        <h1 className="mt-6 font-display text-6xl md:text-8xl leading-[0.95] tracking-tight">
          <MaskLines lines={["Multi-State Railway", <span key="s" className="italic text-gold">Advertising Network</span>]} start={0.1} />
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }} className="mt-8 text-cream/80 text-lg max-w-2xl leading-relaxed">
          Twins Media operates a wide-ranging railway advertising network across multiple states in South India, providing
          premium advertising opportunities at high-footfall railway stations and strategic railway locations.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.65 }} className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
          {[[String(stateCount), "States"], [`${stationCount}+`, "Rail Locations"], [String(RAILWAY_MEDIA.length), "Media Formats"]].map(([n, l]) => (
            <div key={l} className="border-l border-gold/40 pl-4">
              <div className="font-display text-4xl text-gold">{n}</div>
              <div className="text-xs uppercase tracking-widest text-cream/60 mt-1">{l}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const Stations = () => (
  <section className="py-24 md:py-32 bg-cream" data-testid="network-stations">
    <div className="max-w-[1400px] mx-auto px-5 md:px-10">
      <div className="mb-14 max-w-2xl">
        <Reveal><Overline>Key Railway Stations &amp; Locations</Overline></Reveal>
        <Reveal delay={0.05}><h2 className="mt-6 font-display text-5xl md:text-6xl tracking-tight text-charcoal">Where We Advertise</h2></Reveal>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-charcoal/10">
        {RAILWAY_NETWORK.map((s, i) => (
          <Reveal key={s.state} delay={(i % 4) * 0.05}>
            <div className="bg-cream p-8 md:p-9 h-full">
              <span className="font-display text-4xl text-gold">{String(s.stations.length).padStart(2, "0")}</span>
              <h3 className="mt-4 font-display text-2xl md:text-3xl text-charcoal">{s.state}</h3>
              <ul className="mt-6 space-y-2.5">
                {s.stations.map((st) => (
                  <li key={st} className="flex gap-3 text-charcoal-muted text-[15px] leading-relaxed">
                    <span className="mt-2 h-1 w-1 rounded-full bg-gold shrink-0" />
                    {st}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const MediaFormats = () => (
  <section className="py-24 md:py-32 bg-maroon text-cream" data-testid="network-media">
    <div className="max-w-[1400px] mx-auto px-5 md:px-10">
      <div className="mb-14 max-w-2xl">
        <Reveal><Overline dark>Advertising Media Available</Overline></Reveal>
        <Reveal delay={0.05}><h2 className="mt-6 font-display text-5xl md:text-6xl tracking-tight">A Format for Every Campaign</h2></Reveal>
        <Reveal delay={0.1}><p className="mt-6 text-cream/75 text-lg leading-relaxed">We offer a comprehensive range of railway advertising media, across static, illuminated, digital and branded formats.</p></Reveal>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        <Reveal>
          <div className="flex flex-col h-full bg-charcoal text-cream shadow-[0_18px_40px_rgba(0,0,0,0.18)]">
            <div className="flex-1 p-7 md:p-8 flex flex-col justify-between">
              <img src={IMAGES.logo} alt="Twins Media" className="h-14 w-auto self-start bg-cream p-1.5" />
              <div className="mt-8">
                <h3 className="font-display text-3xl md:text-[2.15rem] leading-[1.05] text-gold">Complete Railway Advertising Solutions</h3>
                <p className="mt-4 text-cream/70 text-sm uppercase tracking-[0.15em] leading-relaxed">Premium Media • Maximum Reach • Unmatched Visibility</p>
              </div>
            </div>
            <div className="bg-gold text-maroon px-7 md:px-8 py-3.5 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.2em] font-medium">
              Your Brand <span className="text-maroon/50">•</span> Our Reach
            </div>
          </div>
        </Reveal>
        {RAILWAY_MEDIA.map((m, i) => {
          const Icon = ICON_MAP[m.icon];
          return (
            <Reveal key={m.title} delay={(i % 3) * 0.05}>
              <div className="group flex flex-col h-full bg-cream text-charcoal shadow-[0_18px_40px_rgba(0,0,0,0.18)]">
                <div className="flex-1 p-7 md:p-8">
                  <div className="flex items-start justify-between">
                    <span className="font-display text-3xl text-gold/70">{String(i + 1).padStart(2, "0")}</span>
                    <span className="flex items-center justify-center w-14 h-14 rounded-full border border-maroon/20 text-maroon group-hover:bg-maroon group-hover:text-gold group-hover:border-maroon transition-colors duration-300 shrink-0">
                      {Icon && <Icon size={24} strokeWidth={1.5} />}
                    </span>
                  </div>
                  <h3 className="mt-5 font-display text-2xl leading-snug text-charcoal">{m.title}</h3>
                  <span className="mt-3 block h-px w-10 bg-gold" />
                  <p className="mt-4 text-charcoal-muted text-[15px] leading-relaxed">{m.desc}</p>
                  <ul className="mt-6 space-y-2.5">
                    {m.features.map((f) => {
                      const FIcon = ICON_MAP[f.icon];
                      return (
                        <li key={f.label} className="flex items-center gap-2.5 text-sm text-charcoal">
                          {FIcon && <FIcon size={14} className="text-gold shrink-0" />}
                          {f.label}
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <div className="bg-maroon px-7 md:px-8 py-3.5 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.2em] text-gold">
                  Your Brand <span className="text-cream/40">•</span> Our Reach
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const ReachBand = () => (
  <section className="py-24 md:py-28 bg-cream text-center" data-testid="network-reach">
    <div className="max-w-[1400px] mx-auto px-5 md:px-10">
      <Reveal>
        <span className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-maroon justify-center">
          <span className="h-px w-8 bg-maroon" />Your Brand • Our Reach<span className="h-px w-8 bg-maroon" />
        </span>
        <p className="mt-8 max-w-3xl mx-auto text-charcoal-muted text-lg md:text-xl leading-relaxed">
          With advertising opportunities across multiple railway stations and diverse media formats, Twins Media
          provides brands with extensive visibility among railway passengers, commuters, visitors and the general public.
        </p>
      </Reveal>
    </div>
  </section>
);

const CTA = () => (
  <section className="pb-24 md:pb-32 bg-cream">
    <div className="max-w-[1400px] mx-auto px-5 md:px-10">
      <Reveal>
        <div className="bg-maroon text-cream px-8 md:px-16 py-16 md:py-20 flex flex-col md:flex-row items-center justify-between gap-8 text-center md:text-left">
          <div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight max-w-xl">Twins Media</h2>
            <p className="mt-3 text-cream/70 text-sm uppercase tracking-[0.25em]">Railway • Outdoor • Indoor • Digital Advertising</p>
          </div>
          <Link to="/contact" data-testid="network-cta-btn" className="inline-flex items-center gap-3 bg-gold text-maroon px-10 py-4 text-sm uppercase tracking-[0.18em] hover:bg-gold-hover transition-colors duration-300 shrink-0">
            Get in Touch <ArrowUpRight size={18} />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Network() {
  return (
    <div data-testid="network-page">
      <Seo
        title="Railway Advertising Network — Tamil Nadu, Karnataka, Telangana & Maharashtra"
        description="Twins Media's multi-state railway advertising network spans key stations across Tamil Nadu, Karnataka, Telangana and Maharashtra — hoardings, LED video walls, digital screens, pole kiosks and train branding."
        path="/network"
      />
      <PageHero />
      <Stations />
      <MediaFormats />
      <ReachBand />
      <CTA />
    </div>
  );
}
